import { PUSH_RETRY_FOR_PERIOD } from '@mgCommon/constants';
import { STATE_STATUS, TOAST_TYPE } from '@mgCommon/constants/constants.js';
import { dispatchShowCaToastEvent } from '@mgCommon/utils/utils.js';
import NativePushService from '@mgServices/native-push.service.js';
import { NATIVE_PUSH_CHANNELS } from '@nativePush/constants/constants.js';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const defaultDate = new Date().toJSON().slice(0, 10);
const defaultTime = '00:00';

const initialState = {
  selectedTemplate: {},
  campaignId: '',
  initScheduleData: {},
  hasScheduleChanged: false,
  status: STATE_STATUS.IDLE,
  error: null,
};

export const nativePushCreateCampaign = createAsyncThunk(
  'nativePush/create',
  async ({
    title,
    gameId,
    channel,
    startDate = null,
    startDateTime = null,
    endDate = null,
    endDateTime = null,
    retryFor = 0,
    retryForPeriod = PUSH_RETRY_FOR_PERIOD.WEEKS,
    retryUntilDate = 0,
  }) => {
    const androidPush = [NATIVE_PUSH_CHANNELS.ANDROID, NATIVE_PUSH_CHANNELS.ALL].includes(channel) ? 1 : 0;
    const iosPush = [NATIVE_PUSH_CHANNELS.IOS, NATIVE_PUSH_CHANNELS.ALL].includes(channel) ? 1 : 0;

    const payload = {
      title,
      gameId,
      androidPush,
      iosPush,
      iosPushProduction: iosPush ? 1 : 0,
      iosPushSandbox: iosPush ? 1 : 0,
      startDate: startDate || defaultDate,
      startDateTime: startDateTime || defaultTime,
      endDate: endDate || null,
      endDateTime: endDateTime || null,
      retryFor: retryFor || 0,
      retryForPeriod,
      retryUntilDate: retryUntilDate || 0,
    };
    const res = await NativePushService.createCampaign({ ...payload });
    return res.data;
  }
);

export const nativePushUpdateCampaign = createAsyncThunk(
  'nativePush/update',
  async ({
    title,
    campaignId,
    gameId,
    channel,
    startDate = null,
    startDateTime = null,
    endDate = null,
    endDateTime = null,
    retryFor = 0,
    retryForPeriod = PUSH_RETRY_FOR_PERIOD.WEEKS,
    retryUntilDate = 0,
  }) => {
    const androidPush = [NATIVE_PUSH_CHANNELS.ANDROID, NATIVE_PUSH_CHANNELS.ALL].includes(channel) ? 1 : 0;
    const iosPush = [NATIVE_PUSH_CHANNELS.IOS, NATIVE_PUSH_CHANNELS.ALL].includes(channel) ? 1 : 0;

    const payload = {
      title,
      campaignId,
      gameId,
      androidPush,
      iosPush,
      iosPushProduction: iosPush ? 1 : 0,
      iosPushSandbox: iosPush ? 1 : 0,
      startDate: startDate || defaultDate,
      startDateTime: startDateTime || defaultTime,
      endDate: endDate || null,
      endDateTime: endDateTime || null,
      retryFor: retryFor || 0,
      retryForPeriod,
      retryUntilDate: retryUntilDate || 0,
    };
    const res = await NativePushService.updateCampaign({ ...payload });
    return res.data;
  }
);

export const sendProofingNativePush = createAsyncThunk(
  'nativepush/sendProofingNativePush',
  async ({
    recipients = [],
    gameId,
    campaignId,
    title,
    text,
    action = null,
    url = null,
    pictureUrl = null,
    icon = null,
    nativeOs,
    selectedTemplate = {},
  }) => {
    const campaignIdNumber = !!campaignId && Number.isNaN(Number(campaignId)) ? null : Number(campaignId);
    let proofingMessage = text;

    selectedTemplate?.dynamicVariables?.forEach(d => {
      const replace = `[[${d.name}]]`;
      proofingMessage = proofingMessage.replaceAll(replace, d.proofingValue);
    });

    const payload = {
      playerId: recipients,
      gameId,
      campaignId: campaignIdNumber,
      title,
      text: proofingMessage,
      action,
      url,
      pictureUrl,
      icon,
      nativeOs,
    };

    const res = await NativePushService.sendNativePush({ ...payload });
    return res.data;
  }
);

const nativePushSlice = createSlice({
  name: 'nativePush',
  initialState,
  reducers: {
    setNativePushSelectedTemplate(state, { payload }) {
      state.selectedTemplate = payload;
    },
    setNativePushHasScheduleChanged(state, { payload }) {
      state.hasScheduleChanged = payload.hasScheduleChanged;
    },
    setNativePushCampaignId(state, { payload }) {
      state.campaignId = payload.campaignId;
    },
    setNativePushInitScheduleData(state, { payload }) {
      state.initScheduleData = payload.initScheduleData;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(nativePushCreateCampaign.pending, state => {
        state.status = STATE_STATUS.LOADING;
      })
      .addCase(nativePushCreateCampaign.fulfilled, (state, { payload }) => {
        state.campaignId = payload;
        state.status = STATE_STATUS.SUCCEEDED;
        const successMessage = `Push campaign successfully created with ID: ${payload}`;
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.SUCCESS, message: successMessage });
      })
      .addCase(nativePushCreateCampaign.rejected, (state, { error }) => {
        state.status = STATE_STATUS.FAILED;
        state.error = error.message;
        const errMessage = 'Error during creating campaign';
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.DANGER, message: errMessage });
        console.error(error.message);
      })
      .addCase(nativePushUpdateCampaign.pending, state => {
        state.status = STATE_STATUS.LOADING;
      })
      .addCase(nativePushUpdateCampaign.fulfilled, (state, { payload }) => {
        state.campaignId = payload;
        state.hasScheduleChanged = false;
        state.status = STATE_STATUS.SUCCEEDED;
        const successMessage = `Push campaign successfully updated`;
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.SUCCESS, message: successMessage });
      })
      .addCase(nativePushUpdateCampaign.rejected, (state, { error }) => {
        state.status = STATE_STATUS.FAILED;
        state.error = error.message;
        const errMessage = 'Error during updating campaign';
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.DANGER, message: errMessage });
        console.error(error.message);
      })
      .addCase(sendProofingNativePush.pending, state => {
        state.status = STATE_STATUS.LOADING;
      })
      .addCase(sendProofingNativePush.fulfilled, state => {
        state.status = STATE_STATUS.SUCCEEDED;
        const successMessage = 'Proofing native push successfully sent';
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.SUCCESS, message: successMessage });
      })
      .addCase(sendProofingNativePush.rejected, (state, { error }) => {
        state.status = STATE_STATUS.FAILED;
        state.error = error.message;
        const errMessage = 'Error during sending proofing native push';
        dispatchShowCaToastEvent({ toastType: TOAST_TYPE.DANGER, message: errMessage });
        console.error(error.message);
      });
  },
});

export const {
  setNativePushSelectedTemplate,
  setNativePushHasScheduleChanged,
  setNativePushCampaignId,
  setNativePushInitScheduleData,
} = nativePushSlice.actions;

export default nativePushSlice.reducer;
